import 'project/polyfill'
import { NextPageWithLayout } from '../types/nextPageWithLayout'
import { I18NConfig } from 'next/dist/server/config-shared'
import { createClient } from '../prismicio'
import { getFAQList, getPosts, getReviews } from 'common/cms-sdk'
import { getPlans } from 'common/planData'
import { Utils } from 'common/utils'
import SeoContainer, {
  SeoContainerProps,
} from 'components/util/containers/SeoContainer'

import HeroBanner from 'components/hero/HeroBanner'
import { DesktopBreak } from 'components/DesktopBreak'
import { TBlogPostResult, TPlanSummary, TReviews } from 'common/cms-types'

import { Query } from '@prismicio/types'
import { enterpriseBreadcrumb } from 'components/util/containers/Breadcrumbs'

import HeadingAndText from 'components/HeadingAndText'
import PartnersTestimonial from 'components/cards/new/PartnerTestimonialCard'
import { MailCountTrust } from 'components/MailCountTrust'
import classNames from 'classnames'
import {
  heroGap,
  marginHeader,
  sectionGap,
  sectionPaddingLarge,
} from 'project/padding'
import standardiseTitle from 'project/standardiseTitle'
import SetYourOwnMarginsCard from 'components/cards/new/SetYourOwnMarginsCard'
import EmpowerEnterprises from 'components/cards/new/EmpowerEnterprises'
import WhyChooseEnterpriseCard from 'components/cards/new/WhyChooseEnterpriseCard'
import EnterpriseBenefitsSection from 'components/sections/EnterpriseBenefitsSection'
import PartnersCard from 'components/cards/new/PartnersCard'
import UltimateSaasSolution from 'components/cards/new/UltimateSaasSolution'
import GotYouCoveredCard from 'components/cards/new/GotYouCoveredCard'
import APIDocsCard from 'components/cards/new/APIDocsCard'
import EmbededKonnectForm from 'components/sections/EmbededKonnectForm'
import imageResize from 'project/image-proxy-web'
import FAQList from 'components/util/FAQList'
import HoxtonMixLogo from 'components/icons/HoxtonMixLogo'

export type HomePageType = {
  posts: Query<TBlogPostResult>
  plans: TPlanSummary[]
  reviews: TReviews
}

const HomePage: NextPageWithLayout<HomePageType> = ({
  faqList,
  plans,
  posts,
  reviews,
}) => {
  const seoProps: SeoContainerProps['seoProps'] = {
    description:
      'Integrate virtual and registered office services with our white-label API. Trusted by 100+ banks, accountants, and fintechs.',
    openGraph: {
      url: 'https://www.hoxtonmix.com/',
    },
    title: standardiseTitle(
      'Add Registered Office Address Services to Your Platform',
    ),
  }

  return (
    <SeoContainer
      alternate={[
        {
          href: '/au/partner-integrations',
          lang: 'en-au',
        },
        {
          href: '/partner-integrations',
          lang: 'en-gb',
        },
      ]}
      breadcrumb={enterpriseBreadcrumb()}
      seoProps={seoProps}
    >
      <main
        className={classNames(
          'overflow-x-hidden new-design d-flex flex-column bg-grey6',
          heroGap,
        )}
      >
        <HeroBanner
          logo={<HoxtonMixLogo className='mb-5 mb-lg-8' />}
          hideFooter
          titleSize='small'
          robotImage={{
            alt: 'HaiRo™ robot mascot holding tie',
            dimensions: {
              height: 1039,
              width: 763,
            },
            src: '/images/new/robot-enterprise.png',
          }}
          title='Helping Enterprises Provide Virtual & Registered Office Address Services.'
          cta={
            <a href='#konnect-form' className='btn btn-lg btn-black mt-lg-8'>
              Book a Demo
            </a>
          }
          description={
            <span className='fs-fluid-18-22 fw-semibold d-block pe-lg-6'>
              We provide a full suite of enterprise solutions, ensuring seamless
              integration into your platform. Whether you prefer a plug-and-play
              dashboard or a fully white-label API, our tools are built for
              flexibility.
            </span>
          }
          subtitleContainerClassName='lh-105 fw-semibold mb-8 fs-fluid-24-26'
          subtitle={
            'However you choose to integrate, our enterprise tools have you covered.'
          }
        />
        <div className={classNames('container d-flex flex-column', sectionGap)}>
          <div className={classNames('container d-flex flex-column')}>
            <SetYourOwnMarginsCard />
          </div>
          <div>
            <HeadingAndText
              className={classNames('px-lg-0 px-6 ', marginHeader)}
              classesForTitle='fs-fluid-36-80 lh-105'
              subtitleSize='large'
              title={`How We <span class="color-pink2">Empower Enterprises</span>`}
            />
            <EmpowerEnterprises />
          </div>
          <WhyChooseEnterpriseCard />
          <EnterpriseBenefitsSection />
          <PartnersCard />
          <div>
            <h2
              className={classNames(
                'fs-fluid-36-80 lh-105 px-6 fw-semibold text-center',
                marginHeader,
              )}
            >
              <MailCountTrust highlightClassName='color-pink2' />
            </h2>
            <div className={marginHeader}>
              <PartnersTestimonial />
            </div>
            <div className={'text-center'}>
              <a href='#konnect-form' className='btn btn-lg btn-primary'>
                Book a Demo
              </a>
            </div>
          </div>
          <UltimateSaasSolution />
          <div className={classNames('d-flex flex-column')}>
            <h2
              className={classNames(
                'fw-semibold text-center fs-fluid-36-80 lh-105 whitespace-nowrap',
                marginHeader,
                sectionPaddingLarge,
              )}
            >
              <span>Regulatory Compliance &</span>
              <DesktopBreak />
              <span>Security Excellence.</span>
            </h2>
            <GotYouCoveredCard />
          </div>
          <div className={classNames('d-flex flex-column')}>
            <h2
              className={classNames(
                'fw-semibold text-center fs-fluid-36-80 lh-105',
                marginHeader,
                sectionPaddingLarge,
              )}
            >
              <span className='color-pink2'>API & docs</span>, easy to use,
              security.
            </h2>
            <APIDocsCard />
          </div>
          <EmbededKonnectForm />
          <div className='mb-6'>
            <FAQList slices={faqList.data.slices} />
          </div>
        </div>
      </main>
    </SeoContainer>
  )
}
const PAGE_SIZE = 4

export async function getStaticProps(
  context: I18NConfig & { locale?: string; params: { uid: string } },
) {
  try {
    const client = createClient(context)
    const lang = Utils.getLocaleID(context.locale || context.defaultLocale)
    const plans = await getPlans()
    const posts = await getPosts(client, 1, lang, PAGE_SIZE, undefined)
    const reviews = await getReviews()
    const faqList = await getFAQList(client, lang, 'partnerintegrations')
    return {
      props: {
        faqList,
        plans,
        posts,
        reviews,
      },
    }
  } catch (e) {
    console.log(e)
    return {
      props: {},
    }
  }
}

export default HomePage
